import Hue from '@assets/hue.svg';
import Button from '@atoms/Button/Button';
import Image from '@atoms/Image/Image';
import { IImage } from '@models/IImage';
import { ILink } from '@models/ILink';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import useResizeObserver from '@utils/hooks/useResizeObserver';
import classNames from 'classnames';
import { motion, useScroll, useSpring, useTransform } from 'framer-motion';
import { useRef } from 'react';

interface Props {
  readonly title: string;
  readonly subTitle: string;
  readonly mainImage: IImage;
  readonly secondaryImage?: IImage;
  readonly cta?: ILink;
  readonly glow: boolean;
}

const SimplifiedProductImageGallery = ({
  title,
  subTitle,
  mainImage,
  secondaryImage,
  cta,
  glow,
}: Props) => {
  const isSmallDesktop = useMediaQuery(1440);
  const isMobile = useMediaQuery(1024);
  const [
    mainImageContainerRef,
    { height: mainImageContainerHeight, width: mainImageContainerWidth },
  ] = useResizeObserver();
  const mediaContainerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: mediaContainerRef,
    offset: ['start end', 'end start'],
  });
  const smoothScrollYProgress = useSpring(scrollYProgress, {
    stiffness: 300,
    damping: 50,
  });
  const mainImageTranslateYLimit = 60;
  const mainImageTranslateY = useTransform(
    smoothScrollYProgress,
    [0, 0.5, 1],
    [mainImageTranslateYLimit, 0, -mainImageTranslateYLimit]
  );
  const secondaryImageTranslateYLimit = isSmallDesktop ? 90 : 120;
  const secondaryImageTranslateY = useTransform(
    smoothScrollYProgress,
    [0, 0.5, 1],
    [secondaryImageTranslateYLimit, 0, -secondaryImageTranslateYLimit]
  );

  const hasTwoImages = !!mainImage && !!secondaryImage;

  return (
    <section
      className="component-padding overflow-hidden bg-white pt-13 text-black-100 dark:bg-black-100 dark:text-white lg:pt-[110px]"
      data-testid="CT-14-A-Simplified-Product-Image-Gallery"
    >
      <div
        className="container flex grid-cols-12 flex-col xl:grid"
        ref={mediaContainerRef}
      >
        <div
          className="relative z-10 flex flex-col justify-center gap-[15px] max-xl:items-center max-lg:pb-16 lg:gap-10 xl:col-span-5"
          data-testid="CT-14-A-Simplified-Product-Image-Gallery-Text-Container"
        >
          <h1
            className="mount max-xl:text-center"
            data-testid="CT-14-A-Simplified-Product-Image-Gallery-Title"
          >
            {title}
          </h1>
          <p
            className={classNames(
              'text-grey-450 dark:text-grey-400 max-xl:text-center',
              isMobile ? 'h5' : 'h3'
            )}
            data-testid="CT-14-A-Simplified-Product-Image-Gallery-Subtitle"
          >
            {subTitle}
          </p>
          {cta?.name && cta.url && (
            <Button
              link={cta}
              className="mt-2.5 w-fit xl:mt-4"
              dataTestId="CT-14-A-Simplified-Product-Image-Gallery-CTA"
            />
          )}
        </div>
        <div
          className={classNames(
            'flex justify-end xl:col-span-6 xl:col-start-8',
            { relative: hasTwoImages }
          )}
          data-testid="CT-14-A-Simplified-Product-Image-Gallery-Media-Container"
          ref={mainImageContainerRef}
        >
          <motion.div
            style={{
              ...(hasTwoImages ? { translateY: mainImageTranslateY } : {}),
            }}
            className={classNames({
              'relative max-xl:w-[85%]': hasTwoImages,
            })}
          >
            <Image
              image={mainImage}
              sizes="(min-width: 1024px) 1024px, 100vw"
              dataTestId="CT-14-A-Simplified-Product-Image-Gallery-Main-Image"
            />

            {hasTwoImages && glow && (
              <motion.img
                src={`${Hue}`}
                alt="hue"
                style={{
                  height: mainImageContainerHeight,
                  width: mainImageContainerWidth,
                  translateY: mainImageTranslateY,
                  scale: 2.5,
                  opacity: 0.35,
                }}
                className="absolute bottom-0 -z-max max-h-full xl:-bottom-20 xl:-left-36"
                data-testid="CT-14-A-Simplified-Product-Image-Gallery-Glow-Image"
              />
            )}
          </motion.div>
        </div>

        {hasTwoImages && (
          <motion.div
            style={{ translateY: secondaryImageTranslateY, zIndex: 3 }}
            className="relative xl:col-span-2 xl:col-start-7"
          >
            <div className="absolute -bottom-5 left-0 flex max-h-[86px] max-w-[114px] md:max-h-[180px] md:max-w-[240px] xl:-bottom-20 xl:max-h-[390px] xl:max-w-[470px]">
              <Image
                image={secondaryImage}
                imageClasses="h-full object-contain"
                dataTestId="CT-14-A-Simplified-Product-Image-Gallery-Main-Image"
              />
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default SimplifiedProductImageGallery;
